import { CommonModule } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { PencilSpinnerComponent } from 'src/app/ui/pencil-spinner/pencil-spinner.component';
import { IconConfig, IconTypes, PencilIconComponent } from '../pencil-icon/pencil-icon.component';

export type ButtonIconConfig = Omit<IconConfig, 'name' | 'onClick'>;

/** Shared shape for "one button's worth of config" — reused across dialog @Input()s
 *  (ConfirmationModalConfig's confirmButtonConfig, GraphicModalButtonConfig, etc.) instead of
 *  each one re-declaring the same icon/iconType/label/danger/outlined fields. */
export interface PencilButtonActionConfig {
  icon?: string;
  iconType?: IconTypes;
  label?: string;
  danger?: boolean;
  outlined?: boolean;
}

export enum PencilButtonThemeEnum {
  PRIMARY = 'primary',
  PRIMARY_DARK = 'primary-dark',
  DANGER = 'danger',
  SECONDARY = 'secondary',
  SECONDARY_OUTLINE = 'secondary-outline',
  TERTIARY = 'tertiary',
  TERTIARY_LINK = 'tertiary-link',
  ICON = 'icon',
}

@Component({
  selector: 'app-pencil-button',
  templateUrl: './pencil-button.component.html',
  styleUrls: ['./pencil-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, PencilIconComponent, PencilSpinnerComponent],
})
export class PencilButtonComponent implements AfterContentChecked {
  @Input() selected: boolean = false;
  @Input() disabled = false;
  @Input() showSpinner: boolean = false;
  @Input() icon?: string;
  @Input() iconConfig: ButtonIconConfig = {
    type: IconTypes.svgIcon,
  };
  @Input() theme?: PencilButtonThemeEnum;
  @Input() badge: boolean = false;
  @Input() label?: string;
  @Input() ariaLabel?: string;
  @Input() subtitle?: string;
  @Input() iconOnLeft: boolean = true;
  @Input() buttonType: 'button' | 'submit' | 'reset' = 'submit';
  @Input() supportLongLabel: boolean = false;

  @Output() clickEvent = new EventEmitter<Event>();
  @Output() mouseEnterEvent = new EventEmitter<Event>();
  @Output() mouseLeaveEvent = new EventEmitter<Event>();
  @ContentChild('ng-content') ngContent?: ElementRef;

  @HostBinding('class.selected') get applySelectedClass() {
    return this.selected;
  }
  @HostBinding('class.disabled') get applyDisabledClass() {
    return this.disabled;
  }
  isContentEmpty: boolean = true;

  ngAfterContentChecked() {
    this.isContentEmpty = this.ngContent === undefined;
  }

  handleClick(event: Event): void {
    this.clickEvent.emit(event);
  }

  handleMouseEnter(event: Event): void {
    this.mouseEnterEvent.emit(event);
  }

  handleMouseLeave(event: Event): void {
    this.mouseLeaveEvent.emit(event);
  }
}
